import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { useToast } from "../../../contexts/ToastContext";
import { ColumnDef, ColumnId } from "./callsColumnRegistry";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  allColumns: ColumnDef[];
  visibleColumns: ColumnDef[];
  onToggle: (id: ColumnId) => void;
  onReorder: (orderedIds: ColumnId[]) => void;
  onReset: () => void;
  onClose: () => void;
  /**
   * Columns forced visible by current context (e.g. "label" when hasLabelInteraction
   * is active). Checkbox is rendered checked+disabled, regardless of user toggle.
   */
  forcedVisibleIds?: ColumnId[];
  /** Ref to trigger button — used for focus return on close */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
}

// ─── Sortable row ─────────────────────────────────────────────────────────────

interface SortableColumnRowProps {
  column: ColumnDef;
  isVisible: boolean;
  isLastVisible: boolean;
  /** Column is forced visible by context (e.g. label module active) — checkbox disabled+checked. */
  isForced: boolean;
  isMobile: boolean;
  onToggle: (id: ColumnId) => void;
  onMoveUp: (id: ColumnId) => void;
  onMoveDown: (id: ColumnId) => void;
  isFirst: boolean;
  isLast: boolean;
}

const LAST_VISIBLE_DESC_ID = "last-visible-desc";
const FORCED_DESC_ID = "forced-visible-desc";

const SortableColumnRow: React.FC<SortableColumnRowProps> = ({
  column,
  isVisible,
  isLastVisible,
  isForced,
  isMobile,
  onToggle,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const checkboxId = `col-toggle-${column.id}`;

  // Visible tooltip (native browser) for sighted users when checkbox is disabled.
  // aria-describedby provides the same info to screen readers via sr-only <p>.
  const disabledTitle = isForced
    ? "Wymagana przez aktywny moduł etykiet"
    : isLastVisible
      ? "Wymagana co najmniej jedna widoczna kolumna"
      : undefined;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-3 py-2 rounded hover:bg-neutral-50 ${
        isDragging ? "bg-neutral-100 shadow-sm" : ""
      }`}
    >
      {/* Drag handle — hidden on mobile */}
      {!isMobile && (
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab hover:cursor-grabbing text-neutral-400 hover:text-neutral-600 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
          aria-label={`Przeciągnij aby zmienić kolejność kolumny ${column.label}`}
          aria-roledescription="sortable"
          tabIndex={0}
        >
          <svg
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M8 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 6a2 2 0 1 1 4 0 2 2 0 0 1-4 0zM14 12a2 2 0 1 1 4 0 2 2 0 0 1-4 0zM14 18a2 2 0 1 1 4 0 2 2 0 0 1-4 0z" />
          </svg>
        </button>
      )}

      {/* Mobile up/down buttons */}
      {isMobile && (
        <div className="flex flex-col gap-0.5 flex-shrink-0">
          <button
            onClick={() => onMoveUp(column.id)}
            disabled={isFirst}
            className="text-neutral-400 hover:text-neutral-600 disabled:opacity-30 focus:outline-none focus:ring-1 focus:ring-primary-500 rounded"
            aria-label={`Przenieś kolumnę ${column.label} w górę`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            onClick={() => onMoveDown(column.id)}
            disabled={isLast}
            className="text-neutral-400 hover:text-neutral-600 disabled:opacity-30 focus:outline-none focus:ring-1 focus:ring-primary-500 rounded"
            aria-label={`Przenieś kolumnę ${column.label} w dół`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      )}

      {/* Checkbox — forced takes precedence over last-visible for describedby */}
      <input
        type="checkbox"
        id={checkboxId}
        checked={isForced ? true : isVisible}
        onChange={() => onToggle(column.id)}
        disabled={isForced || isLastVisible}
        aria-describedby={
          isForced
            ? FORCED_DESC_ID
            : isLastVisible
              ? LAST_VISIBLE_DESC_ID
              : undefined
        }
        title={disabledTitle}
        className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
      />

      {/* Label */}
      <label
        htmlFor={checkboxId}
        title={disabledTitle}
        className={`text-sm select-none flex-1 ${
          isForced || isLastVisible
            ? "text-neutral-400 cursor-not-allowed"
            : "text-neutral-700 cursor-pointer"
        }`}
      >
        {column.label}
      </label>
    </li>
  );
};

// ─── Panel ────────────────────────────────────────────────────────────────────

/**
 * Non-modal region for configuring visible columns and their order.
 * Uses @dnd-kit for drag & drop with keyboard accessibility.
 * Mobile (<768px): shows "W górę"/"W dół" buttons instead of drag-handle.
 */
const CallsColumnsPanel: React.FC<Props> = ({
  allColumns,
  visibleColumns,
  onToggle,
  onReorder,
  onReset,
  onClose,
  forcedVisibleIds = [],
  triggerRef,
}) => {
  const forcedSet = new Set(forcedVisibleIds);
  const panelRef = useRef<HTMLDivElement>(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState("");
  const { addToast } = useToast();

  // Detect mobile width (debounced 150ms to avoid per-frame state updates while resizing)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const handler = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setIsMobile(window.innerWidth < 768), 150);
    };
    window.addEventListener("resize", handler);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handler);
    };
  }, []);

  // Close on click outside (mousedown only — useClickOutside does not handle keyboard)
  useClickOutside(panelRef, onClose, true);

  // Close on Escape + return focus to trigger
  // NOTE: kept as separate effect because useClickOutside only listens for mousedown.
  // Merging would require extending the shared hook with a keyboard listener — out of scope.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        triggerRef?.current?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose, triggerRef]);

  // Trap focus on mount: focus the panel
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // IDs for SortableContext (all configurable columns in order)
  const columnIds = allColumns.map((c) => c.id);
  const visibleIds = new Set(visibleColumns.map((c) => c.id));

  // Min-1 check: which column is the last visible
  const lastVisibleId =
    visibleColumns.length === 1 ? visibleColumns[0].id : null;

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = columnIds.indexOf(active.id as ColumnId);
        const newIndex = columnIds.indexOf(over.id as ColumnId);
        const newOrder = arrayMove(columnIds, oldIndex, newIndex);
        onReorder(newOrder);

        // Announce reorder to screen readers
        const movedCol = allColumns.find((c) => c.id === active.id);
        setLiveAnnouncement(
          `Kolumna ${movedCol?.label ?? active.id} przeniesiona na pozycję ${newIndex + 1}`
        );
      }
    },
    [columnIds, onReorder, allColumns]
  );

  // Mobile move up/down
  const handleMoveUp = useCallback(
    (id: ColumnId) => {
      const idx = columnIds.indexOf(id);
      if (idx <= 0) return;
      const newOrder = arrayMove(columnIds, idx, idx - 1);
      onReorder(newOrder);
      const col = allColumns.find((c) => c.id === id);
      setLiveAnnouncement(
        `Kolumna ${col?.label ?? id} przeniesiona na pozycję ${idx}`
      );
    },
    [columnIds, onReorder, allColumns]
  );

  const handleMoveDown = useCallback(
    (id: ColumnId) => {
      const idx = columnIds.indexOf(id);
      if (idx >= columnIds.length - 1) return;
      const newOrder = arrayMove(columnIds, idx, idx + 1);
      onReorder(newOrder);
      const col = allColumns.find((c) => c.id === id);
      setLiveAnnouncement(
        `Kolumna ${col?.label ?? id} przeniesiona na pozycję ${idx + 2}`
      );
    },
    [columnIds, onReorder, allColumns]
  );

  const handleReset = useCallback(() => {
    onReset();
    addToast({
      message: "Przywrócono domyślny układ kolumn",
      type: "success",
    });
  }, [onReset, addToast]);

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-1 z-50 w-72 bg-white border border-neutral-200 rounded-lg shadow-lg outline-none"
      role="region"
      aria-label="Konfiguracja kolumn"
      tabIndex={-1}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-neutral-100">
        <span className="text-sm font-medium text-neutral-800">
          Kolumny{" "}
          <span className="text-neutral-500 font-normal">
            ({visibleColumns.length} z {allColumns.length})
          </span>
        </span>
        <button
          onClick={onClose}
          className="text-neutral-400 hover:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
          aria-label="Zamknij panel konfiguracji kolumn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Accessibility: min-1 hint */}
      <p
        id={LAST_VISIBLE_DESC_ID}
        className="sr-only"
      >
        Wymagana co najmniej jedna widoczna kolumna
      </p>

      {/* Accessibility: forced-visible hint (e.g. label module active) */}
      <p
        id={FORCED_DESC_ID}
        className="sr-only"
      >
        Wymagana przez aktywny moduł etykiet
      </p>

      {/* Columns list */}
      <div className="max-h-80 overflow-y-auto py-1">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={columnIds}
            strategy={verticalListSortingStrategy}
          >
            <ul className="list-none m-0 p-0">
              {allColumns.map((col, idx) => (
                <SortableColumnRow
                  key={col.id}
                  column={col}
                  isVisible={visibleIds.has(col.id)}
                  isLastVisible={col.id === lastVisibleId}
                  isForced={forcedSet.has(col.id)}
                  isMobile={isMobile}
                  onToggle={onToggle}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  isFirst={idx === 0}
                  isLast={idx === allColumns.length - 1}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-neutral-100">
        <button
          onClick={handleReset}
          className="text-sm text-primary-600 hover:text-primary-800 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
        >
          Przywróć domyślne
        </button>
      </div>

      {/* Live region for screen reader announcements */}
      <div
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {liveAnnouncement}
      </div>
    </div>
  );
};

export default CallsColumnsPanel;
